import Icon from "@iconify/react";
import { useSnackbar } from "notistack";
import { useEffect, useRef } from "react";
import { MIconButton } from "src/theme";
import closeFill from "@iconify-icons/eva/close-fill";
import { useDispatch } from "src/redux/store";
import useSessionAuth from "src/hooks/useSessionAuth";
import { getProducts } from "src/redux/slices/product";
import { getCategories } from "src/redux/slices/categories";
import { getInitializeSession } from "src/redux/slices/authJwt";
import { getNotificationFromSession } from "src/redux/slices/notifications";
import { createResumeOrchestrator, attachResumeListeners } from "src/realtime/resumeOrchestrator";
import { refreshStaleConnections } from "src/utils/hubs/HubConnectionManager";

// Comensal QR: sus eventos (session.*) no llevan secuencia ni reenvío, así
// que al volver (pestaña visible, bfcache, foco, red, congelación) se pide
// SIEMPRE el estado de la sesión (snapshot). Fuera > 20 s → se reinician
// también los sockets por si quedaron zombies.
const QR_HUB_RESTART_AFTER_MS = 20_000;

export default function useBackgroundSyncDetectSession() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { session, isSessionAuthenticated } = useSessionAuth();

  const dispatch = useDispatch();

  const activeSessionRef = useRef(session?.id);

  useEffect(() => {
    if (!session || !isSessionAuthenticated) return;

    const orchestrator = createResumeOrchestrator({
      run: async ({ reasons, awayMs }) => {
        if (navigator.onLine === false) return;
        if (awayMs >= QR_HUB_RESTART_AFTER_MS || reasons.includes("freeze") || reasons.includes("pageshow")) {
          refreshStaleConnections(`qr:${reasons.join(",")}`);
        }
        await syncData();
      },
    });
    const detachResume = attachResumeListeners(orchestrator);

    const handleOnline = () => {
      enqueueSnackbar("Conexión restablecida. Sincronizando...", {
        variant: "success",
        action: (key) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <Icon icon={closeFill} />
          </MIconButton>
        ),
      });
    };

    const handleOffline = () => {
      enqueueSnackbar(
        "Atención: No hay conexión a internet. Los cambios no se guardarán.",
        {
          variant: "error",
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        }
      );
    };

    // Listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    //window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      detachResume();
      orchestrator.dispose();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      //window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Mantén actualizada la referencia mutable
  useEffect(() => {
    activeSessionRef.current = session;
  }, [session]);

  // Snapshot de la sesión QR (la referencia evita usar la sesión del
  // primer render, que cambia al pedir/pagar).
  const syncData = () => {
    const ref = activeSessionRef.current;
    const current = ref && typeof ref === "object" ? ref : session;
    if (!current) return Promise.resolve();
    return Promise.allSettled([
      dispatch(getProducts()),
      dispatch(getCategories()),
      dispatch(getNotificationFromSession(current?.id)),
      dispatch(getInitializeSession(current?.isOnlyQr, true)),
    ]);
  };

  return [];
}
